"use client";

import { observer } from "mobx-react-lite";
import { useAppContext } from "../../../shared/functions/Context";
import { useState, useEffect, type FormEvent } from "react";
import MODAL_NAMES from "../ModalName";
import { hideModalFromId } from "../../../shared/functions/ModalShow";
import {
  defaultTrainingRequest,
  type ITrainingRequest,
  type ITrainingType,
} from "../../../shared/models/TrainingRequestModel";
import { dateFormat_YY_MM_DD } from "../../shared/utils/utils";
import type { ITrainingBudget } from "../../../shared/models/TrainingBudgetModal";

import { X, Loader2, AlertCircle } from "lucide-react";
import { Button } from "../../dashboard/company-dashboard/ui/button";
import { Label } from "../../dashboard/company-dashboard/ui/label";
import { Input } from "../../dashboard/company-dashboard/ui/input";
import { Textarea } from "../../dashboard/company-dashboard/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../dashboard/company-dashboard/ui/card";

const CreateCatalogTrainings = observer(() => {
  const { store, api, ui } = useAppContext();
  const departments = store.department.all;

  const [courseRequest, setCourseRequest] = useState<ITrainingRequest>({
    ...defaultTrainingRequest,
  });
  const allUsers = store.user.all.map((user) => user.asJson);
  const [loading, setLoading] = useState(false);

  const allBudget = store.trainingBudget.all;
  const internalBudget = allBudget
    .map((training) => training.asJson)
    .find((training) => training.type === "Internal");
  const externalBudget = allBudget
    .map((training) => training.asJson)
    .find((training) => training.type === "External");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      const trainingRequests: ITrainingRequest[] = allUsers.map((user) => ({
        ...courseRequest,
        uid: user.uid,
        dateRequested: Date.now(),
        supervisor: user.supervisor,
        requestType: "Admin",
        email: user.email ? user.email : "",
      }));

      let sufficientFunds = true;

      if (courseRequest.trainingType === "External" && externalBudget) {
        const availableFunds =
          externalBudget.totalAmount - externalBudget.available;
        if (availableFunds < courseRequest.budget) {
          sufficientFunds = false;
          alert("Warning: Budget funds not available for External Training.");
        }
      } else if (courseRequest.trainingType === "Internal" && internalBudget) {
        const availableFunds =
          internalBudget.totalAmount - internalBudget.available;
        if (availableFunds < courseRequest.budget) {
          sufficientFunds = false;
          alert("Warning: Budget funds not available for Internal Training.");
        }
      }

      if (sufficientFunds) {
        const requests = trainingRequests.map(async (trainingRequest) => {
          return await api.trainingRequest.create(trainingRequest);
        });

        await Promise.all(requests);

        if (courseRequest.trainingType === "External" && externalBudget) {
          const updatedBudget: ITrainingBudget = {
            ...externalBudget,
            available: externalBudget.available - courseRequest.budget, // treat amount as remaining
          };
          await api.trainingBudget.update(updatedBudget);
        } else if (
          courseRequest.trainingType === "Internal" &&
          internalBudget
        ) {
          const updatedBudget: ITrainingBudget = {
            ...internalBudget,
            available: internalBudget.available - courseRequest.budget,
          };
          await api.trainingBudget.update(updatedBudget);
        }
      }
    } catch (error) {
      console.error("Error updating or creating training requests:", error);
    } finally {
      setLoading(false);
      onCancel();
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        await api.trainingBudget.getAll();
        await api.department.getAll();
        await api.user.getAll();
      } catch (error) {
        ui.snackbar.load({
          id: Date.now(),
          message: "Failed to load.",
          type: "danger",
        });
      }
      setLoading(false);
    };
    loadAll();
  }, [api.department, api.user, ui.snackbar]);

  const onCancel = () => {
    store.personalDevelopment.clearSelected();
    setCourseRequest(defaultTrainingRequest);
    hideModalFromId(
      MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_CREATE_TRAINING_CATALOG_MODAL
    );
  };

  const trainingTypeOptions: ITrainingType[] = ["External", "Internal"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-white">
        <CardHeader className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={onCancel}
          >
            <X className="h-4 w-4" />
          </Button>
          <CardTitle className="text-black">
            Create Catalog Training Requests
          </CardTitle>
          <CardDescription className="text-black">
            Add a new training to the catalog for all employees
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Department and Location Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Information</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="department">
                    Department <span className="text-red-500">*</span>
                  </Label>
                  <select
                    id="department"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={courseRequest.department}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        department: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map((department) => (
                      <option
                        key={department.asJson.id}
                        value={department.asJson.id}
                      >
                        {department.asJson.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={courseRequest.location}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        location: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Course Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Course Information</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="courseName">Course Name</Label>
                  <Input
                    id="courseName"
                    value={courseRequest.courseName}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        courseName: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferredDate">Preferred Date</Label>
                  <Input
                    id="preferredDate"
                    type="date"
                    value={
                      courseRequest.preferredDate
                        ? dateFormat_YY_MM_DD(courseRequest.preferredDate)
                        : new Date().toISOString().split("T")[0]
                    }
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        preferredDate: e.target.valueAsNumber,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={
                      courseRequest.startDate
                        ? dateFormat_YY_MM_DD(courseRequest.startDate)
                        : new Date().toISOString().split("T")[0]
                    }
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        startDate: e.target.valueAsNumber,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={
                      courseRequest.endDate
                        ? dateFormat_YY_MM_DD(courseRequest.endDate)
                        : new Date().toISOString().split("T")[0]
                    }
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        endDate: e.target.valueAsNumber,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="offeredBy">Offered By</Label>
                  <Input
                    id="offeredBy"
                    value={courseRequest.offeredBy}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        offeredBy: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    id="duration"
                    value={courseRequest.duration}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        duration: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget">Budget</Label>
                  <Input
                    id="budget"
                    type="number"
                    value={courseRequest.budget}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        budget: e.target.valueAsNumber,
                      })
                    }
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="courseSummary">
                    Brief Summary of Course Content
                  </Label>
                  <Textarea
                    id="courseSummary"
                    rows={3}
                    value={courseRequest.courseSummary}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        courseSummary: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Type of Training Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Type of Training</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-[#84cbe9]" />
                  Please select the type of training.
                </p>
              </div>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={courseRequest.trainingType}
                onChange={(e) =>
                  setCourseRequest({
                    ...courseRequest,
                    trainingType: e.target.value as ITrainingType,
                  })
                }
                required
              >
                <option value="">Select type of training</option>
                {trainingTypeOptions.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Confirmation Checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="confirmation"
                checked={courseRequest.confirmation}
                onChange={(e) =>
                  setCourseRequest({
                    ...courseRequest,
                    confirmation: e.target.checked,
                  })
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="confirmation" className="text-sm font-normal">
                I confirm that this information is true and accurate.
              </Label>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#84cbe9] hover:bg-[#3f6a8e]"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit Request
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
});

export default CreateCatalogTrainings;
